import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { OrganizationAvatar } from "@/components/organization-avatar";
import { useLocation, Link } from "wouter";
import { PageHeaderLayout } from "@/components/layouts/PageHeaderLayout";
import { AppShell } from "@/components/layouts/AppShell";
import ProjectCard from "@/components/project-card";
import NewProjectModal from "@/components/new-project-modal";
import { Button } from "@/components/ui/button";
import { HeaderActionGroup, HeaderSearchInput } from "@/components/layouts/header-slots";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

import { Plus, AlertCircle, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { apiRequest } from "@/lib/queryClient";
import { apiClient } from "@/lib/api-client";
import { useToast } from "@/hooks/use-toast";
import { WeatherWarningWidget } from "@/components/weather-warning-widget";
import type { Project, Ticket } from "@mangelapp/shared";
import { useTranslation } from 'react-i18next';
import { usePermissions } from "@/hooks/use-permissions";
import { PermissionButton } from "@/components/PermissionButton";
import { useCurrentOrganization } from "@/hooks/use-current-organization";
import { AccessDenied } from "@/components/AccessDenied";
import { useProjectStatuses } from "@/hooks/use-project-statuses";

export default function Projects() {
  const { t } = useTranslation(['pages.projects']);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("recent"); // "recent", "name", "active", "paused", "completed"
  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);
  const [showProjectLimitDialog, setShowProjectLimitDialog] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { organizationId } = useCurrentOrganization();

  // Check permissions using the new organization-based system
  const { hasPermission, hasAnyPermission, permissionsLoading } = usePermissions();
  const canCreateProject = hasPermission('manage_projects');

  // Load project statuses for filtering
  const { data: projectStatuses = [] } = useProjectStatuses();

  const { data: projectsResponse, isLoading, error } = useQuery({
    queryKey: ["/api/projects", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/projects?organizationId=${encodeURIComponent(organizationId)}` : "/api/projects";
      const response = await apiRequest(url);
      return await response.json();
    },
    refetchOnWindowFocus: false, // Verhindert unnötige Refreshs
    staleTime: 2 * 60 * 1000, // 2 Minuten Cache für Projects
  });

  const projects = Array.isArray(projectsResponse?.data) ? projectsResponse.data : [];
  const projectsMeta = (projectsResponse as { meta?: { projectsHiddenDueToSubscription?: boolean; hiddenProjectCount?: number } })?.meta;
  const projectsHidden = projectsMeta?.projectsHiddenDueToSubscription === true;
  const hiddenProjectCount = typeof projectsMeta?.hiddenProjectCount === 'number' ? projectsMeta.hiddenProjectCount : 0;
  const hasAssignedProjects = projects.length > 0;
  const canAccessProjectsByPermission = hasAnyPermission([
    'manage_projects',
    'manage_all_projects',
    'view_projects',
    'view_all_projects',
  ]);
  const canAccessProjects = canAccessProjectsByPermission || hasAssignedProjects || projectsHidden;

  // Tickets für Like-Berechnung laden
  const { data: ticketsResponse } = useQuery({
    queryKey: ["/api/tickets", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/tickets?organizationId=${encodeURIComponent(organizationId)}` : "/api/tickets";
      const response = await apiRequest(url);
      return await response.json();
    },
  });

  const allTickets = Array.isArray(ticketsResponse?.data) ? ticketsResponse.data : [];

  // Funktion zur Berechnung der Ticket-Statistiken pro Projekt
  const getProjectTicketStats = (projectId: string) => {
    const projectTickets = allTickets.filter(ticket => ticket.projectId === projectId);
    const totalLikes = projectTickets.reduce((sum, ticket) => {
      const likes = Number((ticket as any).likes);
      return sum + (Number.isFinite(likes) && likes > 0 ? likes : 0);
    }, 0);
    
    return {
      total: projectTickets.length,
      totalLikes
    };
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (project.adresse && project.adresse.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const hasProjectFilters = searchTerm.trim().length > 0 || sortBy !== "recent";

  // Filter by status if status-based sort is selected (use semanticType for language-agnostic matching)
  const statusFilteredProjects = (() => {
    if (sortBy === "active") {
      const activeStatus = projectStatuses.find(
        (s: any) => (s.semanticType || '').toLowerCase() === 'active'
      );
      if (!activeStatus) return filteredProjects;

      return filteredProjects.filter(p => {
        return p.projectStatusId === activeStatus.id || !p.projectStatusId;
      });
    } else if (sortBy === "paused") {
      const pausedStatus = projectStatuses.find(
        (s: any) => (s.semanticType || '').toLowerCase() === 'paused'
      );
      if (!pausedStatus) return [];

      return filteredProjects.filter(p => p.projectStatusId === pausedStatus.id);
    } else if (sortBy === "completed") {
      const completedStatus = projectStatuses.find(
        (s: any) => (s.semanticType || '').toLowerCase() === 'completed'
      );
      if (!completedStatus) return [];

      return filteredProjects.filter(p => p.projectStatusId === completedStatus.id);
    }
    return filteredProjects;
  })();

  const sortedProjects = [...statusFilteredProjects].sort((a, b) => {
    if (sortBy === "name") {
      return a.name.localeCompare(b.name);
    } else if (sortBy === "recent") {
      // Sort by creation date (newer first)
      const aDate = a.erstelltAm ? new Date(a.erstelltAm).getTime() : 0;
      const bDate = b.erstelltAm ? new Date(b.erstelltAm).getTime() : 0;
      return bDate - aDate;
    } else if (sortBy === "active" || sortBy === "paused" || sortBy === "completed") {
      // For status filters, also sort by name within the filtered results
      return a.name.localeCompare(b.name);
    }
    return 0;
  });

  const handleProjectClick = (project: Project) => {
    setLocation(`/project/${project.id}`);
  };

  const createNewProjectMutation = useMutation({
    mutationFn: async () => {
      const newProject = {
        name: t('pages.projects:newProject.defaultName'),
        projektNummer: "",
        beschreibung: "",
        adresse: "",
        managerId: "",
        managerName: "",
        prioritaet: t('pages.projects:newProject.defaultPriority'),
        status: t('pages.projects:newProject.defaultStatus'),
        fortschritt: 0,
        teamMitglieder: []
      };
      
      return await apiClient.createProject(newProject);
    },
    onSuccess: async (newProject: any) => {
      console.log('✅ Neues Projekt erstellt:', newProject);
      // Invalidate and refetch all project queries to ensure fresh data
      await queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key.length > 0 && 
                 (key[0] === "/api/projects" || 
                  (typeof key[0] === 'string' && key[0].startsWith("/api/projects")));
        },
        refetchType: 'all'
      });
      await queryClient.refetchQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key.length > 0 && 
                 (key[0] === "/api/projects" || 
                  (typeof key[0] === 'string' && key[0].startsWith("/api/projects")));
        },
        type: 'all'
      });
      // Direkt zum neuen Projekt navigieren
      const newId = newProject?.data?.id || newProject?.id;
      if (newId) setLocation(`/project/${newId}`);
    },
    onError: (error) => {
      console.error('Project creation error:', error);
    }
  });

  const handleCreateNewProject = () => {
    if (!canCreateProject || projectsHidden) {
      return;
    }

    // Open modal immediately for better UX
    setIsNewProjectModalOpen(true);
    
    // Check project limits in the background (non-blocking)
    // The modal will handle showing the limit dialog if needed
    (async () => {
      try {
        const { apiRequest } = await import("@/lib/queryClient");
        const response = await apiRequest('/api/subscriptions/usage');
        const data = await response.json();
        
        // Handle both response structures (data.data.projects or data.usage)
        const usage = data.data || data.usage;
        const projects = usage?.projects || usage;
        
        if (data.success && projects && projects.current >= projects.max) {
          setIsNewProjectModalOpen(false);
          setShowProjectLimitDialog(true);
        }
      } catch (error) {
        console.error('Failed to check project limits:', error);
        // If check fails, modal stays open (graceful degradation)
      }
    })();
  };


  if (isLoading) {
    return (
      <AppShell>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-lg">{t('pages.projects:loading')}</div>
        </div>
      </AppShell>
    );
  }

  // Keep hook order stable: access checks happen only after all hooks are declared.
  // Membership fallback allows assigned members to open projects even with zero explicit project permissions.
  if (!permissionsLoading && !canAccessProjects) {
    return (
      <AppShell>
        <div className="flex-1">
          <AccessDenied permission={['manage_projects', 'manage_all_projects', 'view_projects', 'view_all_projects']} />
        </div>
      </AppShell>
    );
  }

  return (
    <>
      <AppShell>
      <PageHeaderLayout
        pageTitle={t('pages.projects:title')}
        pageSubtitle={
          projectsHidden
            ? t('pages.projects:subscriptionHidden.subtitleHidden')
            : t('pages.projects:activeCount', { count: projects.length })
        }
        searchComponent={
          <HeaderSearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('pages.projects:searchPlaceholder')}
          />
        }
        actionButtons={
          <HeaderActionGroup>
            {/* Sort Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="pillOutline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  {t('pages.projects:sort.label')}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40 bg-[#E8E8E8] rounded-xl border border-[##DFDFDF] border-[6px] shadow-none">
                <DropdownMenuRadioGroup value={sortBy} onValueChange={setSortBy}>
                  <DropdownMenuRadioItem 
                    value="recent" 
                    className="cursor-pointer data-[state=checked]:bg-blue-600 data-[state=checked]:text-white"
                  >
                    {t('pages.projects:sort.recent')}
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem 
                    value="name" 
                    className="cursor-pointer data-[state=checked]:bg-blue-600 data-[state=checked]:text-white"
                  >
                    {t('pages.projects:sort.name')}
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem 
                    value="active" 
                    className="cursor-pointer data-[state=checked]:bg-blue-600 data-[state=checked]:text-white"
                  >
                    {t('pages.projects:sort.active')}
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem 
                    value="paused" 
                    className="cursor-pointer data-[state=checked]:bg-blue-600 data-[state=checked]:text-white"
                  >
                    {t('pages.projects:sort.paused')}
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem 
                    value="completed" 
                    className="cursor-pointer data-[state=checked]:bg-blue-600 data-[state=checked]:text-white"
                  >
                    {t('pages.projects:sort.completed')}
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* New Project Button */}
            <Button 
              variant="pillOutline" 
              onClick={handleCreateNewProject} 
              size="sm"
              disabled={!canCreateProject || createNewProjectMutation.isPending || projectsHidden}
            >
              <Plus className="h-3 w-3 mr-1" />
              {createNewProjectMutation.isPending ? t('pages.projects:buttons.creating') : t('pages.projects:buttons.new')}
            </Button>
          </HeaderActionGroup>
        }
      >
        {/* Main Content */}
        <div className="flex-1 overflow-auto bg-white p-4 pt-0">
        {projectsHidden && (
          <div className="card-container mb-4 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden />
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-[#333333] mb-1">
                  {t('pages.projects:subscriptionHidden.bannerTitle')}
                </h3>
                <p className="text-xs text-slate-600 mb-3">
                  {hiddenProjectCount > 0
                    ? t('pages.projects:subscriptionHidden.bannerBodyWithCount', { count: hiddenProjectCount })
                    : t('pages.projects:subscriptionHidden.bannerBodyGeneric')}
                </p>
                <Button asChild variant="pillOutline" size="sm" className="h-8">
                  <Link href="/settings?tab=subscription">{t('pages.projects:subscriptionHidden.ctaSubscription')}</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
        {sortedProjects.length === 0 ? (
          <div className="text-center py-12">
            {!projectsHidden && (
              <div className="text-slate-500 text-lg">
                {!canCreateProject && projects.length === 0 && !hasProjectFilters
                  ? t('pages.projects:empty.noAssigned')
                  : t('pages.projects:empty.noResults')}
              </div>
            )}
            {canCreateProject && (
              <Button 
                className="mt-4"
                onClick={handleCreateNewProject}
                disabled={createNewProjectMutation.isPending || projectsHidden}
              >
                {createNewProjectMutation.isPending ? t('pages.projects:buttons.creatingProject') : t('pages.projects:buttons.createFirst')}
              </Button>
            )}
            {!canCreateProject && (
              <div className="text-slate-400 text-sm mt-4">
                {t('pages.projects:empty.noPermission')}
              </div>
            )}
          </div>
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 300px))', justifyContent: 'start' }}>
            {sortedProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                ticketStats={getProjectTicketStats(project.id)}
                onClick={() => handleProjectClick(project)}
                squareImage={true}
              />
            ))}
          </div>
        )}
        </div>
      </PageHeaderLayout>
    </AppShell>

    {/* Modals */}
    <NewProjectModal
      isOpen={isNewProjectModalOpen}
      onClose={() => {
        setIsNewProjectModalOpen(false);
        try {
          queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
          queryClient.refetchQueries({ queryKey: ["/api/projects"] });
        } catch {}
      }}
    />

    {/* Project Limit Error Dialog */}
    <Dialog open={showProjectLimitDialog} onOpenChange={setShowProjectLimitDialog}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {t('pages.projects:errors.projectLimitReached')}
              </DialogTitle>
              <DialogDescription className="mt-1 text-sm text-gray-600">
                {t('pages.projects:errors.projectLimitDescription')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="mt-6 flex justify-end">
          <Button 
            onClick={() => setShowProjectLimitDialog(false)}
            className="h-8 px-4 text-sm font-medium"
          >
            {t('common:ok')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
